import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  PlusCircle,
  FileText,
  Calendar,
  TestTube,
  X,
  ChevronLeft,
  ChevronRight,
  Film,
  Monitor,
  Video,
  Settings
} from 'lucide-react';
import clsx from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  isMobile: boolean;
}

interface NavItem {
  name: string;
  icon: React.ElementType;
  path: string;
  external?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar, isMobile }) => {
  const navItems: NavItem[] = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Create Workshop', icon: PlusCircle, path: '/create-workshop' },
    { name: 'Workshops', icon: Calendar, path: '/workshops' },
    { name: 'Workflows', icon: FileText, path: '/workflows' },
    { name: 'Sessions', icon: FileText, path: '/sessions' },
    { name: 'User Admin', icon: Users, path: '/users' },
    { name: 'Audit Logs', icon: FileText, path: '/audit-logs' },
    { name: 'Configurations', icon: Settings, path: '/configurations' },
  ];

  const videosUrl = import.meta.env.VITE_SIDEBAR_VIDEOS_URL;
  const cobrowsingUrl = import.meta.env.VITE_SIDEBAR_COBROWSING_URL;
  const videoconfUrl = import.meta.env.VITE_SIDEBAR_VIDEOCONF_URL;

  if (videosUrl) {
    navItems.push({ name: 'Videos', icon: Film, path: videosUrl, external: true });
  }
  if (cobrowsingUrl) {
    navItems.push({ name: 'Co-Browsing', icon: Monitor, path: cobrowsingUrl, external: true });
  }
  if (videoconfUrl) {
    navItems.push({ name: 'Video Conf', icon: Video, path: videoconfUrl, external: true });
  }

  const ENV = import.meta.env.VITE_APP_ENV || 'dev';

  if (ENV === 'local' || ENV === 'dev') {
    navItems.push({ name: 'Test Api', icon: TestTube, path: '/test-api' })
  }

  const sidebarClasses = clsx(
    "h-screen bg-white border-r border-gray-200 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0",
    // Mobile: fixed positioning
    isMobile ? "fixed left-0 top-0 z-40" : "sticky top-0",
    {
      "w-64": isOpen && !isMobile,
      "w-20": !isOpen && !isMobile,
      "translate-x-0 w-64": isOpen && isMobile,
      "-translate-x-full": !isOpen && isMobile,
    }
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50"
          onClick={toggleSidebar}
        />
      )}

      <aside className={sidebarClasses}>
        {/* Logo Area */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
          {isOpen || isMobile ? (
            <span className="text-xl font-bold text-gray-800 truncate">AI Proto Admin</span>
          ) : (
            <span className="text-xl font-bold text-gray-800 mx-auto">AP</span>
          )}

          {!isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
            >
              {isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </button>
          )}

          {isMobile && (
            <button onClick={toggleSidebar} className="p-1 text-gray-500">
              <X size={24} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {navItems.map((item) => {
              const commonClasses = clsx(
                "flex items-center px-3 py-2.5 rounded-lg transition-colors duration-200 group",
                !isOpen && !isMobile && "justify-center"
              );

              if (item.external) {
                return (
                  <li key={item.path}>
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={clsx(commonClasses, "text-gray-600 hover:bg-gray-50 hover:text-gray-900")}
                      title={!isOpen && !isMobile ? item.name : undefined}
                    >
                      <item.icon
                        size={22}
                        className={clsx(
                          "flex-shrink-0 transition-colors",
                          (isOpen || isMobile) && "mr-3"
                        )}
                      />
                      {(isOpen || isMobile) && (
                        <span className="font-medium truncate">{item.name}</span>
                      )}
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      clsx(
                        commonClasses,
                        isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                      )
                    }
                    title={!isOpen && !isMobile ? item.name : undefined}
                  >
                    <item.icon
                      size={22}
                      className={clsx(
                        "flex-shrink-0 transition-colors",
                        (isOpen || isMobile) && "mr-3"
                      )}
                    />
                    {(isOpen || isMobile) && (
                      <span className="font-medium truncate">{item.name}</span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer / User Info could go here if not in header */}
        <div className="p-4 border-t border-gray-100">
          {/* Maybe version info or settings link */}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
